import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Box, Group, Text, Badge, ActionIcon, Menu } from '@mantine/core'
import { IoChatbubbleOutline, IoEllipsisHorizontal } from 'react-icons/io5'
import AmmoScoreBadge from './AmmoScoreBadge'
import { useStore } from '../../stores/store'
import { toast } from 'react-toastify'
import agent from '../../api/agent'
import { FlagTargetType } from '../../models/flag'

export const POST_TYPE_LABELS: Record<number, string> = {
  0: 'Blog',
  1: 'Review',
  2: 'Ownership Tip',
  3: 'Discussion'
}
export const POST_TYPE_COLORS: Record<number, string> = {
  0: 'blue',
  1: 'cyan',
  2: 'orange',
  3: 'gray'
}

export interface TagDto {
  id: string;
  name: string;
  displayName: string;
  category: number;
  status: number;
  usageCount: number;
  isSystemTag: boolean;
}

export interface PostCardProps {
  id: string;
  title: string;
  excerpt?: string;
  type: number;
  coverImageUrl?: string | null;
  /** When set (e.g. gallery main), used for the left thumbnail before coverImageUrl. */
  mainPhotoUrl?: string | null;
  authorUsername: string;
  authorDisplayName: string;
  authorImage?: string | null;
  authorTier?: string;
  authorScore?: number;
  voteScore: number;
  currentUserVote?: number | null;
  commentCount?: number;
  createdAt: string;
  tags?: TagDto[];
  rating?: number | null;
  variant?: 'default' | 'compact' | 'featured';
}

const BTN_STYLE = {
  background: 'var(--gs-surface2)',
  border: '1px solid var(--gs-border-bright)',
  color: 'var(--gs-text)',
  padding: '2px 7px',
  borderRadius: 3,
  fontSize: 10
}

function getInitials (displayName?: string | null, username?: string) {
  if (displayName?.trim()) {
    const parts = displayName.trim().split(/\s+/)
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    return displayName.slice(0, 2).toUpperCase()
  }
  if (username?.trim()) return username.trim().slice(0, 2).toUpperCase()
  return '?'
}

function relativeDate (iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`
  if (diff < 86400 * 30) return `${Math.floor(diff / (86400 * 7))}w ago`
  const d = new Date(iso)
  return `${d.toLocaleString('default', { month: 'short' })} ${d.getFullYear()}`
}

export default function PostCard (props: PostCardProps) {
  const {
    id,
    title,
    excerpt,
    type,
    coverImageUrl,
    mainPhotoUrl,
    authorUsername,
    authorDisplayName,
    authorImage = null,
    authorTier = 'Newcomer',
    authorScore = 0,
    voteScore: initialVoteScore,
    currentUserVote = null,
    commentCount = 0,
    createdAt,
    tags,
    rating = null,
    variant = 'default'
  } = props

  const { voteStore, userStore } = useStore()
  const [displayScore, setDisplayScore] = useState(initialVoteScore)
  const [displayVote, setDisplayVote] = useState<number | null>(currentUserVote ?? null)
  const [hovered, setHovered] = useState(false)

  const displayAuthorName = authorDisplayName?.trim() || authorUsername?.trim() || 'Member'
  const thumbUrl = mainPhotoUrl || coverImageUrl

  const handleVote = async (value: number) => {
    const prevScore = displayScore
    const prevVote = displayVote
    const delta = value === displayVote ? -value : value - (displayVote ?? 0)
    setDisplayScore((s) => s + delta)
    setDisplayVote(value === displayVote ? null : value)
    try {
      if (value === displayVote) {
        await voteStore.removeVote(0, id)
      } else {
        await voteStore.castVote(0, id, value)
      }
    } catch {
      setDisplayScore(prevScore)
      setDisplayVote(prevVote)
    }
  }

  const submitReport = async (reason: string) => {
    const r = reason.trim()
    if (!r) {
      toast.error('Please add a reason.')
      return
    }
    try {
      await agent.Flags.submit({ targetType: FlagTargetType.Post, targetId: id, reason: r })
      toast.success('Report submitted.')
    } catch {
      /* interceptor */
    }
  }

  const visibleTags = tags?.filter((t) => t.status === 1).slice(0, 3) ?? []
  const hiddenTagCount = (tags?.filter((t) => t.status === 1).length ?? 0) - visibleTags.length

  return (
    <Box
      sx={{ position: 'relative' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {userStore.isLoggedIn && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 3,
            opacity: hovered ? 1 : 0,
            transition: 'opacity 120ms',
            pointerEvents: hovered ? 'auto' : 'none'
          }}
          onClick={(e) => e.preventDefault()}
        >
          <Menu shadow="md" width={200}>
            <Menu.Target>
              <ActionIcon size="sm" variant="filled" sx={{ background: 'var(--gs-surface2)', color: 'var(--gs-text)' }} aria-label="Post actions">
                <IoEllipsisHorizontal size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                onClick={() => {
                  const reason = window.prompt('Describe the issue for moderators:')
                  if (reason) void submitReport(reason)
                }}
              >
                Report post
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Box>
      )}

      <Box
        component={Link}
        to={`/blog/${id}`}
        sx={{
          background: 'var(--gs-surface)',
          border: '1px solid var(--gs-border)',
          borderRadius: 8,
          display: 'flex',
          overflow: 'hidden',
          cursor: 'pointer',
          transition: 'border-color 150ms',
          textDecoration: 'none',
          color: 'inherit',
          '&:hover': { borderColor: 'var(--gs-accent)' }
        }}
      >
        <Box
          sx={{
            width: 88,
            minWidth: 88,
            background: thumbUrl ? `url(${thumbUrl}) center/cover` : 'linear-gradient(135deg, var(--gs-surface2), var(--gs-surface))'
          }}
        />

        <Box sx={{ flex: 1, padding: '10px 12px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Group spacing={6} sx={{ alignItems: 'center' }}>
            <Badge size="xs" color={POST_TYPE_COLORS[type] ?? 'gray'}>
              {POST_TYPE_LABELS[type] ?? 'Post'}
            </Badge>
            {type === 1 && rating != null && (
              <span style={{
                fontFamily: 'DM Mono, ui-monospace, monospace',
                fontSize: 10,
                fontWeight: 700,
                color: 'var(--gs-gold)',
                letterSpacing: 0
              }}>
                {'★'.repeat(rating)}{'☆'.repeat(5 - rating)}
              </span>
            )}
            <Text sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, color: 'var(--gs-muted)' }}>{relativeDate(createdAt)}</Text>
          </Group>

          <Text weight={600} size="sm" sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, lineHeight: 1.35 }} lineClamp={1}>
            {title}
          </Text>

          {excerpt && (
            <Text size="xs" sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, color: 'var(--gs-muted)' }} lineClamp={2}>
              {excerpt}
            </Text>
          )}

          {visibleTags.length > 0 && (
            <Group spacing={4} sx={{ marginTop: 2 }}>
              {visibleTags.map((tag) => (
                <Box
                  key={tag.id}
                  sx={{
                    background: 'rgba(88,166,255,0.1)',
                    border: '1px solid rgba(88,166,255,0.2)',
                    color: 'var(--gs-blue)',
                    padding: '1px 6px',
                    borderRadius: 3,
                    fontSize: 9,
                    fontWeight: 600,
                    fontFamily: 'DM Sans, sans-serif',
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px'
                  }}
                >
                  {tag.displayName || tag.name}
                </Box>
              ))}
              {hiddenTagCount > 0 && (
                <Text sx={{ fontSize: 9, color: 'var(--gs-muted)', fontFamily: 'DM Sans, sans-serif' }}>+{hiddenTagCount}</Text>
              )}
            </Group>
          )}

          <Group position="apart" spacing="xs" sx={{ marginTop: 'auto' }}>
            <Group spacing={6}>
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  overflow: 'hidden',
                  flexShrink: 0,
                  background: authorImage ? `url(${authorImage}) center/cover` : 'var(--gs-surface2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 9,
                  fontWeight: 600,
                  color: 'var(--gs-text)'
                }}
              >
                {!authorImage && getInitials(authorDisplayName, authorUsername)}
              </Box>
              <Text size="xs" sx={{ color: 'var(--gs-text)' }} lineClamp={1}>
                {displayAuthorName}
              </Text>
              <AmmoScoreBadge tier={authorTier} score={authorScore} size="sm" />
            </Group>

            <Group spacing={6} data-vote-btn onClick={(e) => e.preventDefault()}>
              <ActionIcon
                size="sm"
                variant="subtle"
                sx={{
                  ...BTN_STYLE,
                  minWidth: 26,
                  ...(displayVote === 1 ? { borderColor: 'var(--gs-accent)', color: 'var(--gs-accent)' } : {})
                }}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleVote(1)
                }}
                aria-label="Upvote"
              >
                <Text weight={800} size="xs" sx={{ lineHeight: 1 }}>
                  +
                </Text>
              </ActionIcon>
              <Text size="xs" sx={{ minWidth: 16, textAlign: 'center', fontSize: 10, fontWeight: 600 }}>
                {displayScore}
              </Text>
              <ActionIcon
                size="sm"
                variant="subtle"
                sx={{
                  ...BTN_STYLE,
                  minWidth: 26,
                  ...(displayVote === -1 ? { borderColor: 'var(--gs-accent)', color: 'var(--gs-accent)' } : {})
                }}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleVote(-1)
                }}
                aria-label="Downvote"
              >
                <Text weight={800} size="xs" sx={{ lineHeight: 1 }}>
                  −
                </Text>
              </ActionIcon>
              <Box
                component={Link}
                to={`/blog/${id}#comments`}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  marginLeft: 4,
                  padding: '2px 8px',
                  borderRadius: 4,
                  border: '1px solid var(--gs-border)',
                  background: 'var(--gs-surface2)',
                  color: 'var(--gs-accent)',
                  fontSize: 10,
                  fontWeight: 600,
                  fontFamily: 'DM Sans, sans-serif',
                  textDecoration: 'none',
                  '&:hover': { borderColor: 'var(--gs-accent)' }
                }}
              >
                <IoChatbubbleOutline size={12} />
                {commentCount > 0 ? commentCount : 'Comments'}
              </Box>
            </Group>
          </Group>
        </Box>
      </Box>
    </Box>
  )
}
